import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiUrl } from '../config/api';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';
import InventoryBarcodeModal from './InventoryBarcodeModal';
import AddInventoryModal from './AddInventoryModal';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

interface InventoryItem {
  id: number;
  inventory_id: string;
  imei_mac_address: string;
  category: 'mobile_phone' | 'laptop' | 'tablet' | 'accessories';
  phone_model: string;
  branch_id: number;
  branch_name?: string;
  buying_price: number;
  selling_price: number;
  service_expense: number;
  package_expense: number;
  status: 'ready' | 'reserved' | 'service' | 'sold';
  date_added: string;
  supplier?: {
    id: number;
    name: string;
    phone_number: string;
    aadhar?: string;
    address?: string;
  };
  acquisition_notes?: string;
  payment_method?: 'cash' | 'bank_transfer' | 'upi';
}

interface Supplier {
  id: number;
  name: string;
  phone_number: string;
  aadhar?: string;
  address?: string;
}

interface Branch {
  id: number;
  branch_name: string;
}

const InventoryManagement: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  
  // Date Range Filter States
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('All Time');
  const [isTimeFilterOpen, setIsTimeFilterOpen] = useState(false);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const timeFilterRef = useRef<HTMLDivElement>(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<InventoryItem | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isTransferBranchModalOpen, setIsTransferBranchModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState({
    service_expense: '',
    package_expense: ''
  });
  const [selectedTransferBranch, setSelectedTransferBranch] = useState('');
  
  // Data state
  const [inventoryData, setInventoryData] = useState<InventoryItem[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasMore: false
  });
  const [summary, setSummary] = useState({
    readyToSell: 0,
    inService: 0,
    totalLandedCost: 0
  });
  
  // Modal dropdown states
  const [isModalCategoryDropdownOpen, setIsModalCategoryDropdownOpen] = useState(false);
  const [isModalBranchDropdownOpen, setIsModalBranchDropdownOpen] = useState(false);
  
  // Time filter options for inventory (focused on date_added)
  const timeFilterOptions = [
    'All Time',
    'This Month',
    'Last Month', 
    'This Year',
    'Last Year',
    'Custom'
  ];

  // Handle time filter selection
  const handleTimeFilterSelect = (option: string) => {
    setIsTimeFilterOpen(false);
    if (option === 'Custom') {
      // Set default custom range if not already set
      if (!tempStartDate || !tempEndDate) {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        setTempEndDate(today.toISOString().split('T')[0]);
        setTempStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
      }
      setIsCustomDateModalOpen(true);
    } else {
      setSelectedTimeFilter(option);
      setIsCustomDateRange(false);
    }
  };

  // Handle custom date range modal
  const handleCustomDateApply = () => {
    if (tempStartDate && tempEndDate) {
      setCustomStartDate(tempStartDate);
      setCustomEndDate(tempEndDate);
      setSelectedTimeFilter('Custom');
      setIsCustomDateRange(true);
      setIsCustomDateModalOpen(false);
    }
  };

  const handleCustomDateCancel = () => {
    setIsCustomDateModalOpen(false);
    // Reset temp dates to current custom dates
    setTempStartDate(customStartDate);
    setTempEndDate(customEndDate);
  };
  const [isModalStatusDropdownOpen, setIsModalStatusDropdownOpen] = useState(false);
  const [isModalPaymentMethodDropdownOpen, setIsModalPaymentMethodDropdownOpen] = useState(false);
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);

  // Supplier search states
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSearchResults, setSupplierSearchResults] = useState<Supplier[]>([]);
  const [isSearchingSupplier, setIsSearchingSupplier] = useState(false);
  const [hasSearchedSupplier, setHasSearchedSupplier] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });
  
  // Modal form states
  const [selectedModalCategory, setSelectedModalCategory] = useState('mobile_phone');
  const [selectedModalBranch, setSelectedModalBranch] = useState('');
  const [selectedModalStatus, setSelectedModalStatus] = useState('ready'); // Default to 'ready'
  const [selectedModalPaymentMethod, setSelectedModalPaymentMethod] = useState('cash');
  const [selectedSupplier, setSelectedSupplier] = useState<number | null>(null);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    imei_mac_address: '',
    phone_model: '',
    buying_price: '',
    selling_price: '',
    acquisition_notes: '',
    supplier_name: '',
    supplier_phone: '',
    supplier_aadhar: '',
    supplier_address: '',
    date_added: new Date().toISOString().split('T')[0]
  });


  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Dropdown states
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  
  // Refs for filter dropdowns
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Dropdown options
  const categoryOptions = [
    'All',
    'Mobile Phone',
    'Laptop',
    'Tablet',
    'Accessories'
  ];

  const statusOptions = [
    'All',
    'Ready',
    'Reserved',
    'Service',
    'Sold'
  ];
  
  // Modal dropdown options
  const modalCategoryOptions = [
    { value: 'mobile_phone', label: 'Mobile Phone' },
    { value: 'laptop', label: 'Laptop' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'accessories', label: 'Accessories' }
  ];
  
  const modalStatusOptions = [
    { value: 'ready', label: 'Ready' },
    { value: 'service', label: 'Service' },
    { value: 'reserved', label: 'Reserved' }
  ];
  
  
  const modalPaymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' }
  ];
  
  // Load data on component mount and when filters change
  useEffect(() => {
    if (token && user) {
      fetchInventory(1); // Reset to page 1 when filters change
      fetchBranches();
      fetchSuppliers();
    }
  }, [token, user, categoryFilter, statusFilter, selectedBranchId, isAllBranches, itemsPerPage, selectedTimeFilter, isCustomDateRange, customStartDate, customEndDate]);

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
      // Close time filter dropdown if clicking outside
      if (timeFilterRef.current && !timeFilterRef.current.contains(event.target as Node)) {
        setIsTimeFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Helper functions for UI display
  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      'mobile_phone': 'Mobile Phone',
      'laptop': 'Laptop',
      'tablet': 'Tablet',
      'accessories': 'Accessories'
    };
    return labels[category] || category;
  };
  
  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      'ready': 'Ready',
      'reserved': 'Reserved',
      'service': 'Service',
      'sold': 'Sold'
    };
    return labels[status] || status;
  };
  
  const getSelectedCategoryLabel = () => {
    const category = modalCategoryOptions.find(cat => cat.value === selectedModalCategory);
    return category ? category.label : selectedModalCategory;
  };
  
  
  const getSelectedStatusLabel = () => {
    const status = modalStatusOptions.find(stat => stat.value === selectedModalStatus);
    return status ? status.label : selectedModalStatus;
  };
  
  const getSelectedPaymentLabel = () => {
    const payment = modalPaymentMethodOptions.find(pay => pay.value === selectedModalPaymentMethod);
    return payment ? payment.label : selectedModalPaymentMethod;
  };
  
  // Search function
  const handleSearch = () => {
    fetchInventory(1); // Reset to page 1 when searching
  };

  // Data fetching functions
  const fetchInventory = async (page: number = currentPage) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', itemsPerPage.toString());
      
      if (categoryFilter !== 'All') {
        params.append('category', categoryFilter.toLowerCase().replace(' ', '_'));
      }
      if (statusFilter !== 'All') {
        params.append('status', statusFilter.toLowerCase());
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
        console.log('🔍 Branch filtering applied:', { isAllBranches, selectedBranchId, selectedBranchName });
      } else {
        console.log('🌐 Showing all branches:', { isAllBranches, selectedBranchId, selectedBranchName });
      }
      
      // Add date filtering
      if (selectedTimeFilter !== 'All Time') {
        if (isCustomDateRange && customStartDate && customEndDate) {
          params.append('date_from', customStartDate);
          params.append('date_to', customEndDate);
          params.append('date_range', 'custom');
        } else {
          params.append('date_range', selectedTimeFilter);
        }
      }
      
      console.log('📡 Inventory API call:', getApiUrl(`inventory?${params.toString()}`));
      const response = await fetch(getApiUrl(`inventory?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Transform data to match UI expectations
          const transformedData = data.inventory.map((item: any) => ({
            ...item,
            branch_name: item.branches?.branch_name || 'Unknown Branch',
            supplier: item.suppliers // Map suppliers (plural from backend) to supplier (singular for frontend)
          }));
          setInventoryData(transformedData);
          setPagination(data.pagination);
          if (data.summary) {
            setSummary(data.summary);
          }
          setCurrentPage(page);
        }
      } else {
        setError('Failed to fetch inventory data');
      }
    } catch (err) {
      setError('Error loading inventory data');
      console.error('Inventory fetch error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBranches = async () => {
    try {
      const response = await fetch(getApiUrl("branches"), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBranches(data.branches || []);
          // Set default branch for branch managers
          if (user?.role === 'branch_manager' && data.branches?.length > 0) {
            setSelectedModalBranch(data.branches[0].id.toString());
          }
        }
      }
    } catch (err) {
      console.error('Branches fetch error:', err);
    }
  };
  
  const fetchSuppliers = async () => {
    try {
      const response = await fetch(getApiUrl("suppliers"), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuppliers(data.suppliers || []);
        }
      }
    } catch (err) {
      console.error('Suppliers fetch error:', err);
    }
  };

  // Calculations - use summary from API for accurate totals across all inventory
  const totalItems = pagination.total;
  const readyToSell = summary.readyToSell;
  const inService = summary.inService;
  const totalInventoryValue = summary.totalLandedCost;

  // Use inventory data directly (filtering and pagination are done server-side)
  const currentData = inventoryData;

  // Helper functions

  // Edit item handler
  const handleEditItem = (item: InventoryItem) => {
    // Prevent editing sold items
    if (item.status === 'sold') {
      showToast('Cannot edit sold inventory items. Sold items are locked to maintain data integrity.', 'warning');
      return;
    }

    setSelectedItem(item);
    // Pre-populate form data for editing
    setFormData({
      imei_mac_address: item.imei_mac_address,
      phone_model: item.phone_model,
      buying_price: item.buying_price.toString(),
      selling_price: (item.selling_price || 0).toString(),
      acquisition_notes: item.acquisition_notes || '',
      supplier_name: item.supplier?.name || '',
      supplier_phone: item.supplier?.phone_number || '',
      supplier_aadhar: item.supplier?.aadhar || '',
      supplier_address: item.supplier?.address || '',
      date_added: item.date_added || new Date().toISOString().split('T')[0]
    });
    setSelectedModalCategory(item.category);
    setSelectedModalBranch(item.branch_id.toString());
    setSelectedModalStatus(item.status);
    setSelectedModalPaymentMethod(item.payment_method || 'cash');
    
    // Handle supplier selection
    if (item.supplier && item.supplier.id) {
      setSelectedSupplier(item.supplier.id);
      setIsNewSupplier(false);
    } else {
      setSelectedSupplier(null);
      setIsNewSupplier(false); // Don't default to new supplier mode for items without suppliers
    }
    
    setIsEditModalOpen(true);
  };

  // View inventory barcode handler
  const handleViewInventory = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsBarcodeModalOpen(true);
  };

  // Delete item handler - opens confirmation modal
  const handleDeleteItem = (item: InventoryItem) => {
    // Prevent deleting sold items
    if (item.status === 'sold') {
      showToast('Cannot delete sold inventory items. Sold items are locked to maintain data integrity.', 'warning');
      return;
    }
    
    setItemToDelete(item);
  };

  // Confirm delete handler - actual deletion
  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      setIsDeletingItem(true);
      
      const response = await fetch(getApiUrl(`inventory/${itemToDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Refresh inventory data
        fetchInventory();
        showToast('Item deleted successfully!', 'success');
        setItemToDelete(null);
      } else {
        const errorData = await response.json();
        showToast(`Error: ${errorData.error}`, 'error');
      }
    } catch (err) {
      console.error('Delete inventory error:', err);
      showToast('Failed to delete inventory item', 'error');
    } finally {
      setIsDeletingItem(false);
    }
  };

  const handleAddExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || selectedItems.length === 0) return;

    try {
      const response = await fetch(getApiUrl("inventory/bulk-expense"), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_ids: selectedItems,
          service_expense: parseFloat(expenseFormData.service_expense) || 0,
          package_expense: parseFloat(expenseFormData.package_expense) || 0
        })
      });

      if (response.ok) {
        showToast('Expenses updated successfully', 'success');
        setIsAddExpenseModalOpen(false);
        setExpenseFormData({ service_expense: '', package_expense: '' });
        setSelectedItems([]);
        fetchInventory();
      } else {
        const errorData = await response.json();
        console.error('Add expense error response:', errorData);
        showToast(errorData.details || errorData.error || 'Failed to add expenses', 'error');
      }
    } catch (error) {
      console.error('Error adding expenses:', error);
      showToast('Error adding expenses', 'error');
    }
  };

  const handleTransferBranchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || selectedItems.length === 0 || !selectedTransferBranch) return;
    
    console.log('🔄 Transfer Branch Debug:', {
      selectedItems,
      selectedItemsTypes: selectedItems.map(id => ({ id, type: typeof id })),
      selectedTransferBranch,
      selectedTransferBranchType: typeof selectedTransferBranch,
      token: token ? 'present' : 'missing',
      apiUrl: getApiUrl("inventory/transfer-bulk")
    });
    
    try {
      const response = await fetch(getApiUrl("inventory/transfer-bulk"), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_ids: selectedItems.map(id => Number(id)),
          target_branch_id: Number(selectedTransferBranch)
        })
      });
      
      console.log('🔄 Transfer Response Status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔄 Transfer Success:', result);
        showToast('Items transferred successfully', 'success');
        setIsTransferBranchModalOpen(false);
        setSelectedTransferBranch('');
        setSelectedItems([]);
        fetchInventory();
      } else {
        const errorData = await response.json();
        console.error('🔄 Transfer Error Response:', errorData);
        showToast(errorData.error || errorData.message || 'Failed to transfer items', 'error');
      }
    } catch (error) {
      console.error('🔄 Transfer Network Error:', error);
      showToast('Error transferring items', 'error');
    }
  };

  // CSV Export function
  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      
      // Build the same parameters used for fetching data
      const params = new URLSearchParams();
      
      // Category Filter
      if (categoryFilter !== 'All') {
        params.append('category', categoryFilter.toLowerCase().replace(' ', '_'));
      }
      
      // Status Filter  
      if (statusFilter !== 'All') {
        params.append('status', statusFilter.toLowerCase());
      }
      
      // Search Term
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Branch Selection
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
      }
      
      // Export Flag (tells server to return all records, not paginated)
      params.append('export', 'true');
      params.append('format', 'csv');
      
      try {
        // Try server-side export first
        const response = await fetch(getApiUrl(`inventory/export?${params.toString()}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          // Server-side export successful
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          
          // Generate filename with filters
          const currentDate = new Date().toISOString().split('T')[0];
          let filename = `inventory-export-${currentDate}`;
          
          if (categoryFilter !== 'All') filename += `-${categoryFilter.toLowerCase().replace(' ', '-')}`;
          if (statusFilter !== 'All') filename += `-${statusFilter.toLowerCase()}`;
          if (!isAllBranches && selectedBranchName !== 'All Branches') {
            filename += `-${selectedBranchName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
          }
          if (searchTerm) filename += '-filtered';
          
          // Download file
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.csv`;
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          showToast('Inventory exported successfully!', 'success');
          return;
        }
      } catch (serverError) {
        console.warn('Server-side export failed, falling back to client-side export:', serverError);
      }
      
      // Fallback to client-side export if server-side fails
      console.log('Using client-side export fallback');
      
      // Define CSV headers
      const headers = [
        'Inventory ID',
        'Model',
        'IMEI/MAC Address',
        'Category',
        'Branch',
        'Supplier',
        `Buying Price (${getCurrencySymbol()})`,
        `Service Expense (${getCurrencySymbol()})`,
        `Package Expense (${getCurrencySymbol()})`,
        `Selling Price (${getCurrencySymbol()})`,
        'Status',
        'Payment Method',
        'Acquisition Notes'
      ];

      // Convert inventory data to CSV rows
      const csvRows = inventoryData.map(item => [
        item.inventory_id || 'N/A',
        item.phone_model || '',
        item.imei_mac_address || '',
        item.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || '',
        item.branch_name || '',
        item.supplier?.name || 'No Supplier',
        item.buying_price?.toLocaleString('en-IN') || '0',
        item.service_expense?.toLocaleString('en-IN') || '0',
        item.package_expense?.toLocaleString('en-IN') || '0',
        item.selling_price?.toLocaleString('en-IN') || '0',
        item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || '',
        item.payment_method?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || '',
        item.acquisition_notes || ''
      ]);

      // Combine headers and data
      const csvContent = [headers, ...csvRows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with current date and filters
      const currentDate = new Date().toISOString().split('T')[0];
      let filename = `inventory-export-${currentDate}`;
      
      // Add filter info to filename
      if (categoryFilter !== 'All') {
        filename += `-${categoryFilter.toLowerCase().replace(' ', '-')}`;
      }
      if (statusFilter !== 'All') {
        filename += `-${statusFilter.toLowerCase()}`;
      }
      if (!isAllBranches && selectedBranchName !== 'All Branches') {
        filename += `-${selectedBranchName.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;
      }
      if (searchTerm) {
        filename += '-filtered';
      }
      
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast(`Exported ${inventoryData.length} inventory items to CSV (current page data)`, 'success');
      
    } catch (error) {
      console.error('Export error:', error);
      showToast('Failed to export inventory data', 'error');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Form submission handler for modal
  const handleModalSubmit = async (payload: any) => {
    try {
      setLoading(true);

      // Handle new_supplier -> supplier_info mapping
      if (payload.new_supplier) {
        payload.supplier_info = payload.new_supplier;
        delete payload.new_supplier;
      }

      const url = selectedItem ? getApiUrl(`inventory/${selectedItem.id}`) : getApiUrl("inventory");
      const method = selectedItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsAddModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedItem(null);

          // Refresh inventory data
          fetchInventory();

          showToast(`Inventory item ${selectedItem ? 'updated' : 'added'} successfully!`, 'success');
        }
      } else {
        const errorData = await response.json();
        console.error('Edit/Add inventory error response:', errorData);
        showToast(errorData.details || errorData.error || `Failed to ${selectedItem ? 'update' : 'add'} inventory item`, 'error');
      }
    } catch (err) {
      console.error(`${selectedItem ? 'Edit' : 'Add'} inventory error:`, err);
      showToast(`Failed to ${selectedItem ? 'update' : 'add'} inventory item`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedItem(null);
  };

  // Reset form helper
  const resetForm = () => {
    setFormData({
      imei_mac_address: '',
      phone_model: '',
      buying_price: '',
      selling_price: '',
      acquisition_notes: '',
      supplier_name: '',
      supplier_phone: '',
      supplier_aadhar: '',
      supplier_address: '',
      date_added: new Date().toISOString().split('T')[0]
    });
    setSelectedModalCategory('mobile_phone');
    setSelectedModalBranch('');
    setSelectedModalStatus('ready');
    setSelectedModalPaymentMethod('cash');
    setSelectedSupplier(null);
    setIsNewSupplier(false);
    setSelectedItem(null);
    // Reset supplier search state
    setSupplierSearchTerm('');
    setSupplierSearchResults([]);
    setHasSearchedSupplier(false);
    setImeiError('');
  };

  // Close all modal dropdowns
  const closeAllModalDropdowns = () => {
    setIsModalCategoryDropdownOpen(false);
    setIsModalBranchDropdownOpen(false);
    setIsModalStatusDropdownOpen(false);
    setIsModalPaymentMethodDropdownOpen(false);
    setIsSupplierDropdownOpen(false);
  };

  // Supplier search function - searches by name, phone, or aadhar
  const handleSupplierSearch = () => {
    const searchTerm = supplierSearchTerm.trim().toLowerCase();

    if (!searchTerm) {
      setSupplierSearchResults([]);
      setHasSearchedSupplier(false);
      return;
    }

    setIsSearchingSupplier(true);
    setHasSearchedSupplier(true);

    // Client-side search through suppliers
    const results = suppliers.filter(supplier => {
      const nameMatch = supplier.name?.toLowerCase().includes(searchTerm);
      const phoneMatch = supplier.phone_number?.toLowerCase().includes(searchTerm);
      const aadharMatch = supplier.aadhar?.toLowerCase().includes(searchTerm);
      return nameMatch || phoneMatch || aadharMatch;
    });

    setSupplierSearchResults(results);
    setIsSearchingSupplier(false);
  };

  // Clear selected supplier
  const handleClearSupplier = () => {
    setSelectedSupplier(null);
    setSupplierSearchTerm('');
    setSupplierSearchResults([]);
    setHasSearchedSupplier(false);
  };

  // Auto-fill supplier details when phone number is 10 digits
  const handleSupplierPhoneChange = useCallback((phone: string) => {
    // Extract only digits
    const digitsOnly = phone.replace(/\D/g, '');

    // When phone reaches 10 digits, search for existing supplier
    if (digitsOnly.length === 10) {
      const existingSupplier = suppliers.find(s => {
        const supplierDigits = s.phone_number?.replace(/\D/g, '') || '';
        return supplierDigits === digitsOnly || supplierDigits.endsWith(digitsOnly);
      });

      if (existingSupplier) {
        setFormData(prev => ({
          ...prev,
          supplier_phone: phone,
          supplier_name: existingSupplier.name || '',
          supplier_aadhar: existingSupplier.aadhar || '',
          supplier_address: existingSupplier.address || ''
        }));
        showToast(`Found existing supplier: ${existingSupplier.name}`, 'info');
        return;
      }
    }

    setFormData(prev => ({...prev, supplier_phone: phone}));
  }, [suppliers, showToast]);

  // IMEI validation state (only validated on blur)
  const [imeiError, setImeiError] = useState('');

  const validateImeiOnBlur = () => {
    const requiresValidation = selectedModalCategory === 'mobile_phone' || selectedModalCategory === 'tablet';
    if (requiresValidation && formData.imei_mac_address) {
      const digits = formData.imei_mac_address.replace(/\D/g, '').length;
      if (digits !== 15) {
        setImeiError(`IMEI must be 15 digits (currently ${digits})`);
      } else {
        setImeiError('');
      }
    } else {
      setImeiError('');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'ready': 'bg-green-100 text-green-800',
      'reserved': 'bg-yellow-100 text-yellow-800', 
      'service': 'bg-red-100 text-red-800',
      'sold': 'bg-gray-100 text-gray-800',
      // Also support capitalized versions
      'Ready': 'bg-green-100 text-green-800',
      'Reserved': 'bg-yellow-100 text-yellow-800',
      'Service': 'bg-red-100 text-red-800',
      'Sold': 'bg-gray-100 text-gray-800'
    };
    return statusConfig[status as keyof typeof statusConfig] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      'Mobile Phone': (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      'Laptop': (
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      'Tablet': (
        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      'Accessories': (
        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      // Also support database values (lowercase with underscores)
      'mobile_phone': (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      'laptop': (
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      'tablet': (
        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      'accessories': (
        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    };
    return icons[category as keyof typeof icons] || icons['mobile_phone'];
  };

  const handleSelectAll = () => {
    const currentPageItemIds = currentData.map(item => item.id);
    const allCurrentPageSelected = currentPageItemIds.every(id => selectedItems.includes(id));
    
    if (allCurrentPageSelected) {
      // Deselect all items from current page
      setSelectedItems(prev => prev.filter(id => !currentPageItemIds.includes(id)));
    } else {
      // Select all items from current page
      setSelectedItems(prev => [...prev, ...currentPageItemIds.filter(id => !prev.includes(id))]);
    }
  };

  const handleSelectItem = (itemId: number) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Icons
  const SearchIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const FilterIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
    </svg>
  );

  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const ExportIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );

  const PackageIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );

  return (
    <>
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-500 mt-1">
            Manage your product inventory across all branches
            {!isAllBranches && selectedBranchId ? (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                📍 Filtering: {selectedBranchName}
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                🌐 Showing: All Branches
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <button 
            onClick={handleExportCSV}
            disabled={isExporting}
            className="w-full sm:w-auto px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 transition-colors h-10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="whitespace-nowrap">Exporting...</span>
              </>
            ) : (
              <>
                <ExportIcon />
                <span className="whitespace-nowrap">Export Inventory</span>
              </>
            )}
          </button>
          <button 
            onClick={() => {
              setSelectedItem(null);
              resetForm();
              setIsAddModalOpen(true);
            }}
            className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors h-10"
          >
            <PackageIcon />
            <span className="whitespace-nowrap">Add Inventory</span>
          </button>
        </div>
      </div>


      {/* Branch Filter Info */}
      {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Branch Filter Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Showing only inventory from <strong>{selectedBranchName}</strong>. 
                Select "All Branches" to see inventory from all locations.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Items</p>
              <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ready to Sell</p>
              <p className="text-2xl font-bold text-gray-900">{readyToSell}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">In Service</p>
              <p className="text-2xl font-bold text-gray-900">{inService}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Landed Cost</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalInventoryValue)}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search - Left side */}
          <div className="flex items-center gap-3 max-w-md">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Inventory ID, model, or IMEI.."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                style={{ width: '360px' }}
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors h-10 whitespace-nowrap flex items-center justify-center"
            >
              Search
            </button>
          </div>

          {/* Filters - Right side */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Filter Dropdown */}
            <div className="relative" ref={timeFilterRef}>
              <button 
                onClick={() => setIsTimeFilterOpen(!isTimeFilterOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {selectedTimeFilter === 'Custom' && isCustomDateRange 
                    ? `${customStartDate} to ${customEndDate}` 
                    : selectedTimeFilter
                  }
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isTimeFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isTimeFilterOpen && (
                <div className="absolute left-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {timeFilterOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeFilterSelect(option)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        selectedTimeFilter === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category Dropdown */}
            <div className="relative" ref={categoryDropdownRef}>
              <button 
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {categoryFilter === 'All' ? 'All Categories' : categoryFilter}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isCategoryDropdownOpen && (
                <div className="absolute left-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {categoryOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCategoryFilter(option);
                        setIsCategoryDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        categoryFilter === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option === 'All' ? 'All Categories' : option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Status Dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              <button 
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {statusFilter === 'All' ? 'All Status' : statusFilter}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isStatusDropdownOpen && (
                <div className="absolute left-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {statusOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setStatusFilter(option);
                        setIsStatusDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        statusFilter === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option === 'All' ? 'All Status' : option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('All');
                setStatusFilter('All');
                setSelectedTimeFilter('All Time');
                setIsCustomDateRange(false);
                setIsCategoryDropdownOpen(false);
                setIsStatusDropdownOpen(false);
                setIsTimeFilterOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-lg hover:border-gray-300 bg-white h-10"
            >
              <FilterIcon />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-primary-900">
                {selectedItems.length} item(s) selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => {
                  // Show current values for reference but keep input fields empty
                  const selectedInventoryItems = inventoryData.filter(item => selectedItems.includes(item.id));
                  
                  // Clear the form so users enter exact replacement values
                  setExpenseFormData({
                    service_expense: '',
                    package_expense: ''
                  });
                  
                  setIsAddExpenseModalOpen(true);
                }}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                Update Expenses
              </button>
              <button 
                onClick={() => setIsTransferBranchModalOpen(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
              >
                Transfer Branch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
          <LoadingSpinner size="md" text="Loading inventory data..." />
        </div>
      )}
      
      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 rounded-xl p-6 border border-red-200">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      {!loading && !error && (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={currentData.length > 0 && currentData.every(item => selectedItems.includes(item.id))}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Added</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model & IMEI</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buy Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sell Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Expense</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No inventory items found</h3>
                      <p className="text-gray-500 max-w-sm">
                        {searchTerm || categoryFilter !== 'All' || statusFilter !== 'All' 
                          ? 'No items match your current filters. Try adjusting your search or filters.'
                          : 'Get started by adding your first inventory item to track your stock.'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {item.date_added ? new Date(item.date_added).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleViewInventory(item)}
                      className="text-sm font-mono text-primary-600 bg-primary-50 hover:bg-primary-100 hover:text-primary-800 px-2 py-1 rounded transition-colors cursor-pointer"
                      title="Click to view inventory details & barcode"
                    >
                      {item.inventory_id || 'N/A'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.phone_model}</div>
                      <button 
                        onClick={() => {
                          setSelectedItem(item);
                          setIsDetailsModalOpen(true);
                        }}
                        className="text-xs text-primary-600 hover:text-primary-900 font-mono"
                      >
                        IMEI/MAC: {item.imei_mac_address}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="p-1 bg-gray-100 rounded">
                        {getCategoryIcon(item.category)}
                      </div>
                      <span className="text-sm text-gray-900">{getCategoryLabel(item.category)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.branch_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {formatCurrency(item.buying_price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatCurrency(item.selling_price || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {formatCurrency(Number(item.service_expense || 0) + Number(item.package_expense || 0))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(item.status)}`}>
                      {getStatusLabel(item.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleViewInventory(item)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Inventory & Barcode"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleEditItem(item)}
                        className={`${
                          item.status === 'sold' 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        title={item.status === 'sold' ? 'Cannot edit sold items' : 'Edit Item'}
                        disabled={item.status === 'sold'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDeleteItem(item)}
                        className={`${
                          item.status === 'sold' 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-red-600 hover:text-red-900'
                        }`}
                        title={item.status === 'sold' ? 'Cannot delete sold items' : 'Delete Item'}
                        disabled={item.status === 'sold'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-4 sm:mb-0">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">
              entries (Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total})
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchInventory(Math.max(1, pagination.page - 1))}
              disabled={pagination.page <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, index) => {
              const page = pagination.page <= 3 ? index + 1 : pagination.page - 2 + index;
              if (page > pagination.totalPages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => fetchInventory(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    page === pagination.page
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => fetchInventory(Math.min(pagination.totalPages, pagination.page + 1))}
              disabled={pagination.page >= pagination.totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Add/Edit Inventory Modal */}
      <AddInventoryModal
        isOpen={isAddModalOpen || isEditModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        branches={branches}
        suppliers={suppliers}
        editItem={selectedItem}
        loading={loading}
        showToast={showToast}
      />

      {/* Delete Confirmation Modal */}
      {itemToDelete && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Inventory Item</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete the item with IMEI <strong>{itemToDelete.imei_mac_address}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setItemToDelete(null)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isDeletingItem}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteItem}
                  disabled={isDeletingItem}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeletingItem && (
                    <LoadingSpinner size="sm" text="" color="white" />
                  )}
                  {isDeletingItem ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Expense Modal */}
      {isAddExpenseModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Update Expenses</h2>
              <button
                onClick={() => {
                  setIsAddExpenseModalOpen(false);
                  setExpenseFormData({ service_expense: '', package_expense: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Current Expenses Display */}
            {selectedItems.length > 0 && (() => {
              const selectedInventoryItems = inventoryData.filter(item => selectedItems.includes(item.id));
              const firstItem = selectedInventoryItems[0];
              const currentServiceExpense = Number(firstItem?.service_expense || 0);
              const currentPackageExpense = Number(firstItem?.package_expense || 0);
              const currentTotal = currentServiceExpense + currentPackageExpense;
              
              return (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Current Expenses:</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Service:</span>
                      <div className="font-medium">{formatCurrency(currentServiceExpense)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Package:</span>
                      <div className="font-medium">{formatCurrency(currentPackageExpense)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Total:</span>
                      <div className="font-medium text-primary-600">{formatCurrency(currentTotal)}</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <form onSubmit={handleAddExpenseSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Service Expense ({getCurrencySymbol()})
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={expenseFormData.service_expense}
                  onChange={(e) => setExpenseFormData({...expenseFormData, service_expense: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter new service expense amount"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Package Expense ({getCurrencySymbol()})
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={expenseFormData.package_expense}
                  onChange={(e) => setExpenseFormData({...expenseFormData, package_expense: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter new package expense amount"
                />
              </div>

              <div className="text-sm text-gray-600 mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-xs text-blue-700">
                      <strong>Note:</strong> These values will <strong>replace</strong> the current expenses, not add to them.
                      <br />Leave empty to set to {getCurrencySymbol()}0. Updating {selectedItems.length} item(s).
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddExpenseModalOpen(false);
                    setExpenseFormData({ service_expense: '', package_expense: '' });
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Update Expenses
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Transfer Branch Modal */}
      {isTransferBranchModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Transfer Branch</h2>
              <button
                onClick={() => {
                  setIsTransferBranchModalOpen(false);
                  setSelectedTransferBranch('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleTransferBranchSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer to Branch *
                </label>
                <select
                  value={selectedTransferBranch}
                  onChange={(e) => setSelectedTransferBranch(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.branch_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-sm text-gray-600 mb-4">
                Transferring {selectedItems.length} item(s)
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setIsTransferBranchModalOpen(false);
                    setSelectedTransferBranch('');
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Transfer Items
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Inventory Barcode Modal */}
      {selectedItem && (
        <InventoryBarcodeModal
          item={selectedItem}
          isOpen={isBarcodeModalOpen}
          onClose={() => {
            setIsBarcodeModalOpen(false);
            setSelectedItem(null);
          }}
        />
      )}

      {/* Custom Date Range Modal */}
      {isCustomDateModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-xl p-6 w-full max-w-md m-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Select Date Range</h2>
              <button
                onClick={handleCustomDateCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date *
                </label>
                <input
                  type="date"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date *
                </label>
                <input
                  type="date"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              
              {/* Date range info */}
              {tempStartDate && tempEndDate && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Selected range: <span className="font-medium">{tempStartDate}</span> to <span className="font-medium">{tempEndDate}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(() => {
                      const start = new Date(tempStartDate);
                      const end = new Date(tempEndDate);
                      const diffTime = Math.abs(end.getTime() - start.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                    })()}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={handleCustomDateCancel}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomDateApply}
                disabled={!tempStartDate || !tempEndDate}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
    </>
  );
};

export default InventoryManagement;